import { useMemo, useState } from 'react';
import { Avatar, Button, Card, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { Linkedin, Pencil, Plus, Trash2, Twitter, User } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDebounce } from '@shared/hooks/useDebounce';
import { DataTable } from '@shared/components/DataTable';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { authorsApi } from '@api/authors';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { recordAuditAction } from '@features/audit/recordAction';
import { AuthorDrawer } from '../components/AuthorDrawer';
import type { Author } from '../types';

export function AuthorsPage() {
  const { t } = useTranslation(['authors', 'common', 'errors']);
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Author | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['authors', { page, pageSize, q: debouncedSearch }],
    queryFn: () => authorsApi.list({ page, pageSize, q: debouncedSearch }),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (a: Author) => authorsApi.remove(a.id),
    onSuccess: async (_d, a) => {
      toast.success(t('toast.deleted', { name: a.displayName }));
      await qc.invalidateQueries({ queryKey: ['authors'] });
      void recordAuditAction({
        action: 'author.delete',
        resource: 'author',
        resourceId: a.id,
        summary: t('audit.deleted', { name: a.displayName }),
      });
    },
    onError: (err) => {
      toast.error(resolveApiErrorMessage(err));
    },
  });

  const columns = useMemo(
    () => [
      {
        key: 'author',
        title: t('table.columns.author'),
        dataIndex: 'displayName',
        render: (_: unknown, row: Author) => (
          <Space>
            <Avatar
              size={36}
              src={row.avatarUrl ? resolveAssetUrl(row.avatarUrl) : undefined}
              style={{ background: 'var(--ds-brand)', color: 'var(--ds-text-on-brand)', fontWeight: 600 }}
            >
              {row.displayName
                .split(' ')
                .map((p) => p[0])
                .slice(0, 2)
                .join('')
                .toUpperCase() || <User size={14} />}
            </Avatar>
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontWeight: 600 }}>{row.displayName}</div>
              <div style={{ fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                {row.title ?? row.email ?? `/author/${row.slug}`}
              </div>
            </div>
          </Space>
        ),
      },
      {
        key: 'bio',
        title: t('table.columns.bio'),
        dataIndex: 'bio',
        responsive: ['md' as const],
        render: (v?: string) =>
          v ? (
            <span style={{ fontSize: 13 }}>{v.length > 90 ? `${v.slice(0, 87)}…` : v}</span>
          ) : (
            <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>—</span>
          ),
      },
      {
        key: 'social',
        title: t('table.columns.social'),
        width: 140,
        responsive: ['lg' as const],
        render: (_: unknown, row: Author) => (
          <Space size={6}>
            {row.twitter ? (
              <Tooltip title={row.twitter}>
                <Tag icon={<Twitter size={12} />} style={{ margin: 0 }}>
                  {t('table.social.x')}
                </Tag>
              </Tooltip>
            ) : null}
            {row.linkedin ? (
              <Tooltip title={row.linkedin}>
                <Tag icon={<Linkedin size={12} />} style={{ margin: 0 }}>
                  {t('table.social.linkedin')}
                </Tag>
              </Tooltip>
            ) : null}
            {!row.twitter && !row.linkedin ? (
              <span style={{ color: 'var(--ds-text-tertiary)', fontSize: 12 }}>—</span>
            ) : null}
          </Space>
        ),
      },
      {
        key: 'articleCount',
        title: t('table.columns.articleCount'),
        dataIndex: 'articleCount',
        width: 90,
        align: 'right' as const,
        responsive: ['lg' as const],
        render: (n: number) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{n}</span>
        ),
      },
      {
        key: 'actions',
        title: '',
        width: 110,
        render: (_: unknown, row: Author) => (
          <Space>
            <Tooltip title={t('tooltip.edit')}>
              <Button
                size="small"
                icon={<Pencil size={14} />}
                onClick={() => {
                  setEditing(row);
                  setDrawerOpen(true);
                }}
              />
            </Tooltip>
            <Popconfirm
              title={t('delete.title')}
              description={
                row.articleCount > 0
                  ? t('delete.descriptionHasArticles', {
                      name: row.displayName,
                      count: row.articleCount,
                    })
                  : t('delete.descriptionConfirm', { name: row.displayName })
              }
              okText={t('delete.okText')}
              okButtonProps={{ danger: true }}
              cancelText={t('delete.cancelText')}
              onConfirm={() => deleteMutation.mutate(row)}
            >
              <Tooltip title={t('tooltip.delete')}>
                <Button size="small" danger icon={<Trash2 size={14} />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [deleteMutation, t],
  );

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('breadcrumb.taxonomy') },
          { label: t('breadcrumb.authors') },
        ]}
        actions={
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            {t('page.newAuthor')}
          </Button>
        }
      />

      <Card>
        <DataTable<Author>
          data={data?.items ?? []}
          columns={columns}
          rowKey="id"
          loading={isFetching}
          search={{
            value: search,
            onChange: (v) => {
              setSearch(v);
              setPage(1);
            },
            placeholder: t('table.searchPlaceholder'),
          }}
          pagination={{
            page,
            size: pageSize,
            total: data?.total ?? 0,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
          exportable
          exportFileName="authors"
        />
      </Card>

      <AuthorDrawer
        open={drawerOpen}
        author={editing}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}
